import { createRouter, createWebHistory } from "vue-router";
import SitesOverview from "@/components/SitesOverview.vue";
import SiteRoster from "@/components/SiteRoster.vue";
import ReportsView from "@/components/ReportsView.vue";
import PersonLog from "@/components/PersonLog.vue";
import WallDisplay from "@/components/WallDisplay.vue";
import LocationSettings from "@/components/LocationSettings.vue";
import DoorSettings from "@/components/DoorSettings.vue";
import ManualPresence from "@/components/ManualPresence.vue";
import CompanyGroups from "@/components/CompanyGroups.vue";
import ChangeStatus from "@/components/ChangeStatus.vue";
import DisplaySettings from "@/components/DisplaySettings.vue";
import StatusTypes from "@/components/StatusTypes.vue";
import DisplayScreens from "@/components/DisplayScreens.vue";
import UnmappedBadges from "@/components/UnmappedBadges.vue";
import PairScreen from "@/components/PairScreen.vue";
import ArrangeDisplay from "@/components/ArrangeDisplay.vue";
import BadgeMappings from "@/components/BadgeMappings.vue";
import UserSettings from "@/components/UserSettings.vue";
import DepartmentManagers from "@/components/DepartmentManagers.vue";
import DenmarkView from "@/components/Denmark-view.vue";
import TheUSView from "@/components/TheUS-view.vue";
import SingaporeView from "@/components/Singapore-view.vue";
import { isSignedIn, isAdmin } from "@/auth/authService";

const routes = [

    {
        // Admins start at the site directory; everyone else at the dashboard,
        // which is the only one of the two they can open.
        path: '/',
        redirect: () => (isAdmin() ? '/sites' : '/display')
    },
    {
        path: "/sites",
        name: "Sites",
        component: SitesOverview,
        meta: { requiresAuth: true, adminOnly: true },
    },
    {
        // Everyone in a country, across all its offices. Declared BEFORE the
        // office route: Vue matches in order, and the more specific path has to
        // come second or it would never be reached.
        path: "/sites/:country",
        name: "CountryRoster",
        component: SiteRoster,
        meta: { requiresAuth: true, adminOnly: true },
    },
    {
        // Office names are free text from Entra and can contain spaces and
        // punctuation, so the param is URL-encoded by the caller and decoded in
        // the component.
        path: "/sites/:country/:office",
        name: "SiteRoster",
        component: SiteRoster,
        meta: { requiresAuth: true, adminOnly: true },
    },
    {
        path: "/reports",
        name: "Reports",
        component: ReportsView,
        meta: { requiresAuth: true, adminOnly: true },
    },
    {
        // Reached from the reports table (which knows user_id) or the site
        // roster (which only knows the person's Entra email). Both are passed
        // as query params rather than in the path, since either may be absent.
        path: "/log",
        name: "PersonLog",
        component: PersonLog,
        meta: { requiresAuth: true },
    },
    {
        // Hallway screen. Rotates through every site on its own, with no
        // controls and no navigation, since nobody interacts with it. Marked so
        // the app chrome can be hidden.
        path: "/display",
        name: "Display",
        component: WallDisplay,
        meta: { requiresAuth: true, chromeless: true },
    },
    {
        // The signed-in person's own badge history. Same component as the log
        // reached from the reports table, with no parameters, which it reads as
        // "me".
        path: "/registrations",
        name: "Registrations",
        component: PersonLog,
        meta: { requiresAuth: true },
    },
    {
        path: "/settings",
        name: "UserSettings",
        component: UserSettings,
        meta: { requiresAuth: true },
    },
    {
        path: "/my-status",
        name: "ChangeStatus",
        component: ChangeStatus,
        meta: { requiresAuth: true },
    },
    {
        // Where a wall screen is paired. No auth: the screen has nothing yet,
        // and the code it types is the credential. Chromeless because the
        // screen is not a person and has no use for a menu.
        path: "/pair",
        name: "PairScreen",
        component: PairScreen,
        meta: { chromeless: true },
    },
    {
        path: "/admin/unmapped-badges",
        name: "UnmappedBadges",
        component: UnmappedBadges,
        meta: { requiresAuth: true, adminOnly: true },
    },
    {
        path: "/admin/screens",
        name: "Screens",
        component: DisplayScreens,
        meta: { requiresAuth: true, adminOnly: true },
    },
    {
        path: "/admin/statuses",
        name: "StatusTypes",
        component: StatusTypes,
        meta: { requiresAuth: true, adminOnly: true },
    },
    {
        path: "/admin/badges",
        name: "Badges",
        component: BadgeMappings,
        meta: { requiresAuth: true, adminOnly: true },
    },
    {
        path: "/admin/department-managers",
        name: "DepartmentManagers",
        component: DepartmentManagers,
        meta: { requiresAuth: true, adminOnly: true },
    },
    {
        // Arranging one country's board. Not in the menu: it is reached from
        // the country it arranges, which is the only place the choice makes
        // sense.
        path: "/admin/display/:country",
        name: "ArrangeDisplay",
        component: ArrangeDisplay,
        meta: { requiresAuth: true, adminOnly: true },
    },
    {
        path: "/admin/display",
        name: "DisplaySettings",
        component: DisplaySettings,
        meta: { requiresAuth: true, adminOnly: true },
    },
    {
        path: "/admin/manual",
        name: "ManualPresence",
        component: ManualPresence,
        meta: { requiresAuth: true, adminOnly: true },
    },
    {
        path: "/admin/companies",
        name: "CompanyGroups",
        component: CompanyGroups,
        meta: { requiresAuth: true, adminOnly: true },
    },
    {
        path: "/admin/doors",
        name: "Doors",
        component: DoorSettings,
        meta: { requiresAuth: true, adminOnly: true },
    },
    {
        path: "/admin/locations",
        name: "Locations",
        component: LocationSettings,
        meta: { requiresAuth: true, adminOnly: true },
    },
    {
        path: "/Denmark",
        name: "Denmark",
        component: DenmarkView,
        meta: { requiresAuth: true },
    },
    {
        path: "/Singapore",
        name: "Singapore",
        component: SingaporeView,
        meta: { requiresAuth: true },
    },
    {
        path: "/US",
        name: "TheUS",
        component: TheUSView,
        meta: { requiresAuth: true },
    },
];

// Anything unmatched -- including the withdrawn /news and /activity -- goes to
// the site directory rather than rendering nothing.
routes.push({ path: "/:pathMatch(.*)*", redirect: () => (isAdmin() ? "/sites" : "/display") });

const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes,
});

// Route guard.
//
// IMPORTANT: this is a usability control, not a security control. It decides
// what the browser renders, and a determined user can bypass it entirely.
// The backend must independently reject unauthenticated requests -- see
// VERO-SEC-002 in the security register, still open.
//
// Safe to read auth state synchronously here because main.js awaits
// initialize() before mounting the app, so MSAL has already settled.
router.beforeEach((to) => {
    if (to.meta.requiresAuth && !isSignedIn()) {
        return { name: "SignedOut" };
    }

    // Someone already signed in has no use for the login page.
    if (to.name === "SignedOut" && isSignedIn()) {
        return { name: "Sites" };
    }

    // Admin pages. This is a usability control -- the backend refuses the
    // underlying endpoints regardless -- so it exists to avoid showing somebody
    // a page that would only fill with permission errors.
    if (to.meta.adminOnly && !isAdmin()) {
        return { name: "Display" };
    }

    return true;
});

// Landing page for signed-out users. Declared after the guard so it is never
// itself guarded.
router.addRoute({
    path: "/welcome",
    name: "SignedOut",
    component: () => import("@/components/SignedOut.vue"),
});

export default router;
